import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AuthShell, AuthInput, PrimaryButton } from "@/components/auth/AuthShell";

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [info, setInfo] = useState(null);
  const [infoError, setInfoError] = useState("");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    api
      .get(`/auth/invite/${token}`)
      .then((res) => {
        setInfo(res.data);
        setName(res.data.name || "");
      })
      .catch((err) => setInfoError(errorMessage(err, "This invitation is not valid")));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/auth/accept-invite", { token, password, name: name.trim() || undefined, username: username.trim() || undefined });
      applySession(res.data);
      toast.success("Welcome to EMERGE! Your account is ready.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token || infoError) {
    return (
      <AuthShell title="Invitation not valid" subtitle={infoError || "This invitation link is missing its token."}>
        <Link to="/login" className="font-semibold text-primary hover:underline">Go to sign in</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set up your account" subtitle={info ? `You have been invited as ${info.role === "supervisor" ? "a supervisor" : "an admin"} (${info.email}).` : "Checking your invitation..."}>
      {info && (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="accept-invite-form">
          <AuthInput label="Your name" id="name" data-testid="invite-name-input" value={name} onChange={(e) => setName(e.target.value)} required />
          <AuthInput label="Username (optional)" id="username" data-testid="invite-username-input" value={username} onChange={(e) => setUsername(e.target.value)} hint="Letters, numbers, dots, dashes. You can also sign in with your email." />
          <AuthInput label="Password" id="password" type="password" data-testid="invite-password-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required minLength={8} hint="At least 8 characters with a letter and a number." />
          <AuthInput label="Confirm password" id="confirm" type="password" data-testid="invite-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />
          {error && <p className="field-error" role="alert" data-testid="invite-error">{error}</p>}
          <PrimaryButton loading={loading} data-testid="invite-submit-button">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Activate account
          </PrimaryButton>
        </form>
      )}
    </AuthShell>
  );
}
